import { useAuth0 } from "@auth0/auth0-react";
import { useMemo } from "react";

import { type ApiClient, createApiClient } from "./client";

// Auth0 error codes meaning "the session can't be silently refreshed" -- the
// refresh token is missing, expired, or revoked. With
// `useRefreshTokensFallback={false}` (see Auth0Provider) getAccessTokenSilently
// throws one of these instead of falling back to an iframe. Rather than letting
// every query surface a hard "... unavailable" error, we send the user back
// through login (returning them to the current page), the same way
// ProtectedRoute handles a cold unauthenticated load.
const REAUTH_ERROR_CODES = new Set([
  "login_required",
  "missing_refresh_token",
  "invalid_grant",
  "consent_required",
]);

// Several queries can fail their token fetch in the same tick; the first one
// triggers the (full-page) login redirect and the rest no-op so we don't stack
// redirects.
let reauthRedirectInFlight = false;

// Test-only: clear the once-per-page redirect guard between cases so each test
// starts from a clean slate. Production never resets it -- the guard exists for
// the lifetime of the page, which ends at the login redirect.
export function __resetReauthRedirectGuardForTests(): void {
  reauthRedirectInFlight = false;
}

function isReauthError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const code = (error as { error?: unknown }).error;
  if (typeof code === "string" && REAUTH_ERROR_CODES.has(code)) {
    return true;
  }
  // Belt-and-suspenders: the missing-refresh-token error's message is stable
  // even if the structured `error` code shifts across SDK versions.
  const message = (error as { message?: unknown }).message;
  return typeof message === "string" && message.includes("Missing Refresh Token");
}

export function useApiClient(): ApiClient {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } = useAuth0();
  return useMemo(
    () =>
      createApiClient(async () => {
        if (!isAuthenticated) {
          return null;
        }
        try {
          return await getAccessTokenSilently();
        } catch (error) {
          if (isReauthError(error)) {
            if (!reauthRedirectInFlight) {
              reauthRedirectInFlight = true;
              void loginWithRedirect({
                appState: {
                  returnTo: window.location.pathname + window.location.search,
                },
              });
            }
            return null;
          }
          throw error;
        }
      }),
    [getAccessTokenSilently, loginWithRedirect, isAuthenticated],
  );
}
